/**
 * utils
 */

import isPlainObject from 'lodash/isPlainObject';
import currency from 'currency.js';

import store from '@/store';
import axiosInstance from '@/utils/axios.js';
import localI18n from '@/lang/index.js';

const arrayLogicalAnd = (arr, arrCompare) => {
    return arrCompare.every(item => {
        return arr.includes(item);
    });
};

const arrayLogicalOr = (arr, arrCompare) => {
    return arrCompare.some(item => {
        return arr.includes(item);
    });
};

export const authCheck = authToken => {
    const auth = store.state.userAuth || [];
    if (authToken === null || authToken === undefined || authToken === '') {
        // 无需权限
        return true;
    } else if (Array.isArray(authToken)) {
        // 权限是数组，则只需要数组中包含一个权限即认为有权限
        return arrayLogicalOr(auth, authToken);
    } else if (isPlainObject(authToken)) {
        const logical = authToken.logical || 'and';
        const authTokenVal = authToken.value || [];
        if (logical === 'and') {
            // 数组中需包含全部权限
            return arrayLogicalAnd(auth, authTokenVal);
        }
        // 只需要数组中包含一个权限即认为有权限
        return arrayLogicalOr(auth, authTokenVal);
    }

    return auth.includes(authToken);
};

export const checkPropExists = obj => {
    return !(obj === undefined || obj === null);
};

export const checkEmpty = obj => {
    if (obj === undefined || obj === null) {
        return true;
    }
    if (typeof obj === 'boolean') {
        return false;
    }
    if (typeof obj === 'number') {
        return isNaN(obj);
    }
    if (typeof obj === 'string') {
        return obj.trim() === '';
    }
    return _.isEmpty(obj);
};

export const formatMoney = (str, num) => {
    /*
     * 参数说明：
     * str：要格式化的数字
     * num：保留几位小数
     * */
    let n = num,
        s = str;
    // eslint-disable-next-line
    n = n > 0 && n <= 20 ? n : 2;
    s = parseFloat((s + '').replace(/[^\d\.-]/g, '')).toFixed(n) + '';
    const l = s
        .split('.')[0]
        .split('')
        .reverse();
    const r = s.split('.')[1];
    let t = '';
    for (let i = 0; i < l.length; i++) {
        // eslint-disable-next-line
        t += l[i] + ((i + 1) % 3 === 0 && i + 1 !== l.length ? ',' : '');
    }
    return (
        t
            .split('')
            .reverse()
            .join('') +
        '.' +
        r
    );
};

export const optionDateEnd = startDate => {
    return {
        disabledDate(time) {
            return startDate ? time.getTime() < new Date(startDate).getTime() : false;
        }
    };
};

export const optionDateStart = endDate => {
    return {
        disabledDate(time) {
            return endDate ? time.getTime() >= new Date(endDate).getTime() : false;
        }
    };
};

// 文件下载方法通用
export const fileDownload = (url, options = {method: 'GET', data: null}) => {
    return new Promise((res, rej) => {
        axiosInstance({
            url,
            method: options.method || 'GET',
            responseType: 'blob', // important
            ignorShowLoading: true,
            isFileDownload: true,
            params: options.data || {}
        })
            .then(response => {
                const contentDisposition =
                    response.headers['content-disposition'] || response.headers['Content-Disposition'];
                const resMessage = decodeURIComponent(
                    response.headers['x-custom-message'] || response.headers['X-Custom-Message'] || ''
                );

                if (contentDisposition) {
                    // 下载文件，优先以filename*=xxx的名字为下载的文件名，如果没有时，再取filename=xxxx的文件名。参考：https://developer.mozilla.org/zh-CN/docs/Web/HTTP/Headers/Content-Disposition
                    var filename = decodeURIComponent((contentDisposition.match(/filename\*=[\w\W]*''([\w\W]*)/) || [])[1]) || decodeURIComponent((contentDisposition.match(/filename=\"*([\s\S]*);\"*/) || [])[1]);

                    //避免filename解析失败时，下载的文件格式不对的问题
                    if (!filename || filename === 'undefined') {
                        if (response.headers['content-type'] && response.headers['content-type'].indexOf('excel') > 0) {
                            filename = '文件.xls';
                        }
                    }

                    const blobModel = new Blob([response.data]);

                    if (!window.navigator.msSaveOrOpenBlob) {
                        // 非IE浏览器
                        const url = window.URL.createObjectURL(blobModel);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', filename);
                        document.body.appendChild(link);
                        link.click();
                        setTimeout(() => {
                            document.body.removeChild(link);
                        }, 0);
                    } else {
                        // IE 11
                        window.navigator.msSaveOrOpenBlob(blobModel, filename);
                    }

                    res(filename);
                } else if (resMessage) {
                    // 提示错误信息
                    rej(new Error(resMessage));
                } else {
                    rej(new Error(localI18n.t('common.server-error')));
                }
            })
            .catch(error => {
                rej(error);
            });
    });
};

// pdf文件预览通用方法，参数必须为pdf文件的绝对路径
export const previewPdf = url => {
    let i18nValue = store.state.i18nValue;

    if (i18nValue === 'en') {
        i18nValue = 'en-US';
    }

    const pdfViewerUrl = `dist/static/pdfjs/web/viewer.html?file=${encodeURIComponent(url)}#locale=${i18nValue}`;

    // 为了防止浏览器阻止弹出新窗口，使用创建a元素点击的方式实现
    const link = document.createElement('a');
    link.href = pdfViewerUrl;
    link.style.visibility = 'hidden';
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
        document.body.removeChild(link);
    }, 0);
};

export const previewOffice = fileId => {
    return new Promise((res, rej) => {
        axiosInstance({
            url: `/web/v1/preview/${fileId}`,
            method: 'GET',
            ignorShowLoading: true,
            isFileDownload: true
        })
            .then(response => {
                if (response.data.succeed) {
                    const previewUrl = response.data.data;

                    const link = document.createElement('a');
                    link.href = previewUrl;
                    link.style.visibility = 'hidden';
                    link.setAttribute('target', '_blank');
                    link.setAttribute('rel', 'noopener noreferrer');
                    document.body.appendChild(link);
                    link.click();
                    setTimeout(() => {
                        document.body.removeChild(link);
                    }, 0);
                    res();
                } else {
                    rej(new Error(response.data.msg));
                }
            })
            .catch(error => {
                rej(error);
            });
    });
};

// 修改Tab标题，关闭Tab
export const setVmForTabModify = vm => {
    return {
        changeTabTitle({titleKey, params = {}, routeName}) {
            let routeNameStr = routeName;
            if (!routeNameStr) {
                routeNameStr = vm.$route.name;
            }

            vm.$root.$emit('fe-framework:tab:change-title', titleKey, params, routeNameStr);
        },
        recoverTabTitle(routeName) {
            let routeNameStr = routeName;
            if (!routeNameStr) {
                routeNameStr = vm.$route.name;
            }

            vm.$root.$emit('fe-framework:tab:recover-title', routeNameStr);
        },
        closeTab(routeName) {
            let routeNameStr = routeName;
            if (!routeNameStr) {
                routeNameStr = vm.$route.name;
            }

            vm.$root.$emit('fe-framework:tab:close', routeNameStr);
        }
    };
};

export {currency};